import BorderHorizontalOutlinedIcon from '@mui/icons-material/BorderHorizontalOutlined'
import BorderVerticalOutlinedIcon from '@mui/icons-material/BorderVerticalOutlined'
import { Box, IconButton, Tooltip } from '@mui/material'
import { IColumnBorders, IDataSetBorders } from '../../../definitions/report_builder/ReportBuilderDefinitions'
import BorderInnerIcon from '../../icons/BorderInnerIcon'
import BorderLeftIcon from '../../icons/BorderLeftIcon'
import BorderOnlyBottomIcon from '../../icons/BorderOnlyBottomIcon'
import BorderOnlyTopIcon from '../../icons/BorderOnlyTopIcon'
import BorderOutsideIcon from '../../icons/BorderOutsideIcon'
import BorderRightIcon from '../../icons/BorderRightIcon'

interface P {
    borders?: IDataSetBorders | IColumnBorders
    onChange?: (borders: IDataSetBorders | IColumnBorders) => void
    label?: string
    isColumn?: boolean
    isDataSet?: boolean
    formatNumbersPercentage?: boolean
}

const BorderStyleEditor = ({ borders = {}, onChange, isColumn, isDataSet }: P) => {
    const iconSize = '30px'

    const handleDataSetBorderToggle = (borderType: keyof IDataSetBorders) => {
        const newBorders = { ...borders } as IDataSetBorders

        if (borderType === 'inner') {
            const newValue = !newBorders.inner
            newBorders.inner = newValue
            delete newBorders.innerHorizontal
            delete newBorders.innerVertical
        } else if (borderType === 'innerHorizontal' || borderType === 'innerVertical') {
            newBorders[borderType] = !newBorders[borderType]
            delete newBorders.inner

            if (newBorders.innerHorizontal && newBorders.innerVertical) {
                newBorders.inner = true
                delete newBorders.innerHorizontal
                delete newBorders.innerVertical
            }
        } else {
            newBorders[borderType] = !newBorders[borderType]
        }

        onChange?.(newBorders)
    }

    const handleColumnBorderToggle = (borderType: keyof IColumnBorders) => {
        const newBorders = { ...borders } as IColumnBorders
        newBorders[borderType] = !newBorders[borderType]
        onChange?.(newBorders)
    }

    if (isDataSet) {
        const dataSetBorders = borders as IDataSetBorders

        return (
            <Box sx={{ mb: 2 }}>
                {/* <Typography variant='caption' sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
                    Whole Dataset:
                </Typography> */}

                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                    <Tooltip title='Outside border (around entire dataset)'>
                        <IconButton
                            onClick={() => handleDataSetBorderToggle('outside')}
                            sx={{
                                'height': iconSize,
                                'width': iconSize,
                                'border': 1,
                                'borderColor': dataSetBorders.outside ? 'primary.main' : 'divider',
                                'borderRadius': 1,
                                'bgcolor': dataSetBorders.outside ? 'action.selected' : 'transparent',
                                '&:hover': {
                                    borderColor: 'primary.main',
                                    bgcolor: dataSetBorders.outside ? 'action.selected' : 'action.hover'
                                }
                            }}>
                            <BorderOutsideIcon fontSize='small' />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title='All inner borders (horizontal and vertical)'>
                        <IconButton
                            onClick={() => handleDataSetBorderToggle('inner')}
                            sx={{
                                'height': iconSize,
                                'width': iconSize,
                                'border': 1,
                                'borderColor': dataSetBorders.inner ? 'primary.main' : 'divider',
                                'borderRadius': 1,
                                'bgcolor': dataSetBorders.inner ? 'action.selected' : 'transparent',
                                '&:hover': {
                                    borderColor: 'primary.main',
                                    bgcolor: dataSetBorders.inner ? 'action.selected' : 'action.hover'
                                }
                            }}>
                            <BorderInnerIcon fontSize='small' />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title='Inner horizontal borders only'>
                        <IconButton
                            onClick={() => handleDataSetBorderToggle('innerHorizontal')}
                            sx={{
                                'height': iconSize,
                                'width': iconSize,
                                'border': 2,
                                'borderColor': dataSetBorders.innerHorizontal ? 'primary.main' : 'divider',
                                'borderRadius': 1,
                                'bgcolor': dataSetBorders.innerHorizontal ? 'action.selected' : 'transparent',
                                '&:hover': {
                                    borderColor: 'primary.main',
                                    bgcolor: dataSetBorders.innerHorizontal ? 'action.selected' : 'action.hover'
                                }
                            }}>
                            <BorderHorizontalOutlinedIcon fontSize='small' />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title='Inner vertical borders only'>
                        <IconButton
                            onClick={() => handleDataSetBorderToggle('innerVertical')}
                            sx={{
                                'height': iconSize,
                                'width': iconSize,
                                'border': 2,
                                'borderColor': dataSetBorders.innerVertical ? 'primary.main' : 'divider',
                                'borderRadius': 1,
                                'bgcolor': dataSetBorders.innerVertical ? 'action.selected' : 'transparent',
                                '&:hover': {
                                    borderColor: 'primary.main',
                                    bgcolor: dataSetBorders.innerVertical ? 'action.selected' : 'action.hover'
                                }
                            }}>
                            <BorderVerticalOutlinedIcon fontSize='small' />
                        </IconButton>
                    </Tooltip>
                </Box>
                {/*
                <Typography variant='caption' sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
                    Individual Rows:
                </Typography> */}

                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <Tooltip title='Top border'>
                        <IconButton
                            onClick={() => handleDataSetBorderToggle('top')}
                            sx={{
                                height: iconSize,
                                width: iconSize,
                                border: 2,
                                borderColor: dataSetBorders.top ? 'primary.main' : 'divider',
                                borderRadius: 1,
                                bgcolor: dataSetBorders.top ? 'action.selected' : 'transparent'
                            }}>
                            <BorderOnlyTopIcon />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title='Bottom border'>
                        <IconButton
                            onClick={() => handleDataSetBorderToggle('bottom')}
                            sx={{
                                height: iconSize,
                                width: iconSize,
                                border: 2,
                                borderColor: dataSetBorders.bottom ? 'primary.main' : 'divider',
                                borderRadius: 1,
                                bgcolor: dataSetBorders.bottom ? 'action.selected' : 'transparent'
                            }}>
                            <BorderOnlyBottomIcon />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title='Left border'>
                        <IconButton
                            onClick={() => handleDataSetBorderToggle('left')}
                            sx={{
                                height: iconSize,
                                width: iconSize,
                                border: 2,
                                borderColor: dataSetBorders.left ? 'primary.main' : 'divider',
                                borderRadius: 1,
                                bgcolor: dataSetBorders.left ? 'action.selected' : 'transparent'
                            }}>
                            <BorderLeftIcon />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title='Right border'>
                        <IconButton
                            onClick={() => handleDataSetBorderToggle('right')}
                            sx={{
                                height: iconSize,
                                width: iconSize,
                                border: 2,
                                borderColor: dataSetBorders.right ? 'primary.main' : 'divider',
                                borderRadius: 1,
                                bgcolor: dataSetBorders.right ? 'action.selected' : 'transparent'
                            }}>
                            <BorderRightIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
        )
    }

    if (isColumn) {
        const columnBorders = borders as IColumnBorders

        return (
            <Box sx={{ mb: 2 }}>

                {/* <Typography variant='caption' sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
                    Column borders (apply to entire column):
                </Typography> */}

                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <Tooltip title='Top border (header only)'>
                        <IconButton
                            onClick={() => handleColumnBorderToggle('top')}
                            sx={{
                                height: iconSize,
                                width: iconSize,
                                border: 2,
                                borderColor: columnBorders.top ? 'primary.main' : 'divider',
                                borderRadius: 1,
                                bgcolor: columnBorders.top ? 'action.selected' : 'transparent'
                            }}>
                            <BorderOnlyTopIcon />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title='Bottom border (last row only)'>
                        <IconButton
                            onClick={() => handleColumnBorderToggle('bottom')}
                            sx={{
                                height: iconSize,
                                width: iconSize,
                                border: 2,
                                borderColor: columnBorders.bottom ? 'primary.main' : 'divider',
                                borderRadius: 1,
                                bgcolor: columnBorders.bottom ? 'action.selected' : 'transparent'
                            }}>
                            <BorderOnlyBottomIcon />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title='Left border (entire column)'>
                        <IconButton
                            onClick={() => handleColumnBorderToggle('left')}
                            sx={{
                                height: iconSize,
                                width: iconSize,
                                border: 2,
                                borderColor: columnBorders.left ? 'primary.main' : 'divider',
                                borderRadius: 1,
                                bgcolor: columnBorders.left ? 'action.selected' : 'transparent'
                            }}>
                            <BorderLeftIcon />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title='Right border (entire column)'>
                        <IconButton
                            onClick={() => handleColumnBorderToggle('right')}
                            sx={{
                                height: iconSize,
                                width: iconSize,
                                border: 2,
                                borderColor: columnBorders.right ? 'primary.main' : 'divider',
                                borderRadius: 1,
                                bgcolor: columnBorders.right ? 'action.selected' : 'transparent'
                            }}>
                            <BorderRightIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
        )
    }

    return null
}

export default BorderStyleEditor
